import React, { useState, useEffect } from 'react';

const FileUploadManagement = () => {
  const [uploadedFiles, setUploadedFiles] = useState({
    regulations: [],
    partnerships: [],
    documents: [],
    images: []
  });

  const [dragActive, setDragActive] = useState({});

  useEffect(() => {
    // Load saved files from localStorage
    const saved = localStorage.getItem('adminUploadedFiles');
    if (saved) {
      setUploadedFiles(JSON.parse(saved));
    }
  }, []);

  const handleDrag = (e, category) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(prev => ({ ...prev, [category]: true }));
    } else if (e.type === 'dragleave') {
      setDragActive(prev => ({ ...prev, [category]: false }));
    }
  };

  const handleDrop = (e, category) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(prev => ({ ...prev, [category]: false }));

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(e.dataTransfer.files, category);
    }
  };

  const handleFileInput = (e, category) => {
    if (e.target.files && e.target.files[0]) {
      handleFiles(e.target.files, category);
    }
  };

  const handleFiles = (files, category) => {
    const fileArray = Array.from(files).map(file => ({
      id: Date.now() + Math.random(),
      name: file.name,
      size: file.size,
      type: file.type,
      uploadDate: new Date().toISOString(),
      dataUrl: null
    }));

    // Create data URLs for preview
    fileArray.forEach(fileObj => {
      const reader = new FileReader();
      reader.onload = (e) => {
        fileObj.dataUrl = e.target.result;
        setUploadedFiles(prev => {
          const updated = {
            ...prev,
            [category]: [...prev[category], fileObj]
          };
          localStorage.setItem('adminUploadedFiles', JSON.stringify(updated));
          return updated;
        });
      };
      const originalFile = Array.from(files).find(f => f.name === fileObj.name);
      reader.readAsDataURL(originalFile);
    });
  };

  const removeFile = (category, fileId) => {
    setUploadedFiles(prev => {
      const updated = {
        ...prev,
        [category]: prev[category].filter(file => file.id !== fileId)
      };
      localStorage.setItem('adminUploadedFiles', JSON.stringify(updated));
      return updated;
    });
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (type) => {
    if (type.includes('pdf')) return 'fas fa-file-pdf';
    if (type.includes('image')) return 'fas fa-file-image';
    if (type.includes('word')) return 'fas fa-file-word';
    if (type.includes('excel') || type.includes('sheet')) return 'fas fa-file-excel';
    return 'fas fa-file';
  };

  const uploadCategories = [
    {
      key: 'regulations',
      title: 'Regulatory Documents',
      description: 'Upload compliance documents, licenses, and regulatory filings',
      acceptedTypes: '.pdf,.doc,.docx',
      icon: 'fas fa-gavel',
      color: '#dc3545'
    },
    {
      key: 'partnerships',
      title: 'Partnership Materials',
      description: 'Upload partnership agreements, IB materials, and program documents',
      acceptedTypes: '.pdf,.doc,.docx,.ppt,.pptx',
      icon: 'fas fa-handshake',
      color: '#28a745'
    },
    {
      key: 'documents',
      title: 'General Documents',
      description: 'Upload terms of service, privacy policy, and other legal documents',
      acceptedTypes: '.pdf,.doc,.docx,.txt',
      icon: 'fas fa-file-contract',
      color: '#007bff'
    },
    {
      key: 'images',
      title: 'Images & Media',
      description: 'Upload images for pages, banners, and promotional materials',
      acceptedTypes: '.jpg,.jpeg,.png,.svg,.webp',
      icon: 'fas fa-images',
      color: '#ffc107'
    }
  ];

  return (
    <div className="admin-section">
      <div className="section-header">
        <h2>File Upload Management</h2>
        <p>Upload and manage documents, images, and other assets</p>
      </div>

      <div className="upload-categories">
        {uploadCategories.map((category) => (
          <div key={category.key} className="upload-category">
            <div className="category-header">
              <div className="category-icon" style={{ color: category.color }}>
                <i className={category.icon}></i>
              </div>
              <div className="category-info">
                <h3>{category.title}</h3>
                <p>{category.description}</p>
              </div>
            </div>

            <div 
              className={`upload-zone ${dragActive[category.key] ? 'drag-active' : ''}`}
              onDragEnter={(e) => handleDrag(e, category.key)}
              onDragLeave={(e) => handleDrag(e, category.key)}
              onDragOver={(e) => handleDrag(e, category.key)}
              onDrop={(e) => handleDrop(e, category.key)}
            >
              <input
                type="file"
                id={`file-${category.key}`}
                multiple
                accept={category.acceptedTypes}
                onChange={(e) => handleFileInput(e, category.key)}
                style={{ display: 'none' }}
              />
              <label htmlFor={`file-${category.key}`} className="upload-label">
                <i className="fas fa-cloud-upload-alt"></i>
                <div className="upload-text">
                  <strong>Click to upload</strong> or drag and drop
                  <br />
                  <small>Accepted formats: {category.acceptedTypes}</small>
                </div>
              </label>
            </div>

            {uploadedFiles[category.key].length > 0 && (
              <div className="uploaded-files">
                <h4>Uploaded Files ({uploadedFiles[category.key].length})</h4>
                <div className="files-grid">
                  {uploadedFiles[category.key].map((file) => (
                    <div key={file.id} className="file-card">
                      <div className="file-icon">
                        <i className={getFileIcon(file.type)}></i>
                      </div>
                      <div className="file-info">
                        <div className="file-name" title={file.name}>
                          {file.name.length > 20 ? 
                            `${file.name.substring(0, 20)}...` : 
                            file.name
                          }
                        </div>
                        <div className="file-details">
                          <small>{formatFileSize(file.size)}</small>
                          <small>{new Date(file.uploadDate).toLocaleDateString()}</small>
                        </div>
                      </div>
                      <div className="file-actions">
                        {file.dataUrl && file.type.includes('image') && (
                          <button 
                            className="preview-btn"
                            onClick={() => window.open(file.dataUrl, '_blank')}
                          >
                            <i className="fas fa-eye"></i>
                          </button>
                        )}
                        <button 
                          className="download-btn"
                          onClick={() => {
                            const link = document.createElement('a');
                            link.href = file.dataUrl;
                            link.download = file.name;
                            link.click();
                          }}
                        >
                          <i className="fas fa-download"></i>
                        </button>
                        <button 
                          className="delete-btn"
                          onClick={() => removeFile(category.key, file.id)}
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="upload-guidelines">
        <h3>Upload Guidelines</h3>
        <div className="guidelines-grid">
          <div className="guideline-item">
            <i className="fas fa-file-archive"></i>
            <div>
              <strong>File Size:</strong> Maximum 10MB per file
            </div>
          </div>
          <div className="guideline-item">
            <i className="fas fa-shield-alt"></i>
            <div>
              <strong>Security:</strong> Files are stored locally in browser
            </div>
          </div>
          <div className="guideline-item">
            <i className="fas fa-format-text"></i>
            <div>
              <strong>Naming:</strong> Use descriptive, lowercase filenames
            </div>
          </div>
          <div className="guideline-item">
            <i className="fas fa-backup"></i>
            <div>
              <strong>Backup:</strong> Keep original copies as backup
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FileUploadManagement;